import { useOrgAuthStore, useWallet, useWalletModal } from "@solomon-apps/shared";
import { useState } from "react";

import { useClientContextKey } from "@/hooks/useClientContextKey";
import { useClientPositions } from "@/hooks/useClientPositions";
import { useSavedDestinations } from "@/hooks/useClientSavedDestinations";
import {
    findSavedDestination,
    SAME_AS_SOURCE_LABEL,
    savedDestinationLabel
} from "@/pages/RewardDestinations/destinationInput";

import { usePositionDiscovery } from "./components/usePositionDiscovery";
import { usePositionSource } from "./components/usePositionSource";
import { usePositionVerification } from "./components/usePositionVerification";
import { useSourceResolving } from "./components/useSourceResolving";
import { useStepNavigation } from "./components/useStepNavigation";
import { useVerificationPresentation } from "./components/useVerificationPresentation";
import { MULTISIG_VAULT_LABEL } from "./destinationVault";
import {
    hasDuplicatePosition,
    isSourceAlreadyRegistered,
    resolveCandidateSourceAddress,
    resolveSubmitReview,
    type StepGateState
} from "./stepFlowState";
import type { FindSlice, NavSlice, RewardsSlice, SourceSlice, SubmitSlice, VerifySlice } from "./steps";
import { defaultRewardsDestinationAddress, sourceAddress } from "./types";

/** The wizard's full state, grouped one slice per concern (see `steps.ts`). */
export interface StepFlow {
    source: SourceSlice;
    find: FindSlice;
    verify: VerifySlice;
    rewards: RewardsSlice;
    submit: SubmitSlice;
    nav: NavSlice;
}

/** Composes the source, discovery, verification, submission and navigation
 * owners into the public slices consumed by both walkthrough views. */
export function useStepFlow(): StepFlow {
    const { connected, publicKey, disconnect } = useWallet();
    const { openModal } = useWalletModal();
    // Existing positions, to catch a duplicate (same source on the same Program
    // Surface) at the Verify step instead of after signing and reaching Submit.
    const { positions: existingPositions } = useClientPositions();
    // Names the chosen Rewards Destination in the Submit receipt.
    const { destinations: savedDestinations } = useSavedDestinations();
    // Issuer that performs the manual "direct" verification out of band. Solomon
    // is currently the sole issuer, so it's the fallback when /me omits issuerOrg.
    const issuerName = useOrgAuthStore((s) => s.me?.issuerOrg?.name ?? null);

    const connectedWalletAddress = publicKey?.toBase58() ?? "";

    const [address, setAddress] = useState("");
    const [addressTouched, setAddressTouched] = useState(false);

    const source = usePositionSource(address, connectedWalletAddress);
    const { sourceInput, sourceDetection, sourceKind, resolvedMultisig, multisigSummary, handleSourceRetry } = source;

    // Address that would be submitted as `walletAddress` for the current source
    // (EOA → the wallet; multisig → the multisig account). Mirrors
    // `sourceAddress()` so the duplicate check keys on the same value the API does.
    const candidateSourceAddress = resolveCandidateSourceAddress({
        sourceKind,
        enteredAddress: address,
        connectedWalletAddress,
        multisigAddress: resolvedMultisig?.address ?? null
    });
    const discovery = usePositionDiscovery({
        sourceDetection,
        connectedWalletAddress,
        sourceInput,
        existingPositions,
        candidateSourceAddress
    });
    const { matchedSurfaceUuid, findStatus, addressPreview, foundPosition } = discovery;
    const {
        verification,
        verificationError,
        recoveryReason,
        verifyStatus,
        verifiedSource,
        retryReady,
        manualReviewDescription,
        setManualReviewDescription,
        pendingReview,
        handleVerify,
        handleVerifyAgain,
        handleVerifyDirect,
        onAddressChange,
        onAddressBlur,
        destination,
        setDestination,
        handleSubmit,
        claimRequired,
        handleConfirmClaim,
        submitError,
        verificationExpired,
        registeredPositionCount,
        submitted,
        isSubmitting
    } = usePositionVerification({
        address,
        setAddress,
        setAddressTouched,
        source,
        discovery,
        issuerName,
        wallet: { connected, disconnect, connectedWalletAddress, openModal }
    });
    // A position for this (Program Surface, source) pair already exists, so the
    // backend would reject the submit as a conflict. Surfacing it here lets the
    // user fix it at Verify rather than after signing and reaching Submit. Hidden
    // positions are excluded from this list, so the submit-time guard remains the
    // backstop for those.
    const duplicatePositionExists = hasDuplicatePosition({
        matchedSurfaceUuid,
        candidateSourceAddress,
        existingPositions
    });

    // The source-step check: the pasted source (the EOA, or the multisig
    // account for Squads) already has a position with this Company Recipient,
    // on any surface. Known as soon as the field is classified, before any
    // wallet connects or discovery runs.
    const sourceAlreadyRegistered = isSourceAlreadyRegistered({
        sourceKind,
        enteredAddress: sourceInput,
        multisigAddress: resolvedMultisig?.address ?? null,
        existingPositions
    });
    // Every way the source can turn out to be registered renders the same:
    // the field's error state plus an "Already Registered" badge beside the
    // address-type badge, and both verify actions disabled. The discovery-time
    // checks (`findStatus` / `duplicatePositionExists`) stay as backstops.
    const alreadyRegistered = sourceAlreadyRegistered || duplicatePositionExists || findStatus === "already-registered";

    const contextKey = useClientContextKey();
    const sourceResolving = useSourceResolving({
        sourceKey: JSON.stringify([contextKey, sourceInput]),
        phase:
            alreadyRegistered || sourceInput.length === 0
                ? "unavailable"
                : sourceDetection.status === "loading"
                  ? "loading"
                  : sourceKind !== null
                    ? "resolved"
                    : "unavailable",
        addressReady: addressPreview !== null,
        discoverySettled: findStatus !== "idle" && findStatus !== "finding"
    });

    const stepGateState: StepGateState = {
        findStatus,
        matchedSurfaceUuid,
        verifiedSource,
        duplicatePositionExists: alreadyRegistered
    };

    const {
        addressError,
        addressInvalid,
        addressLabel,
        addressPlaceholder,
        findMessage,
        findMessageTone,
        sourceNotice,
        verifyMessage,
        verifyMessageTone,
        verificationIsDirect,
        verifyDirectLabel,
        verifyActionDisabled,
        verifyActionLabel,
        showSourceRetry,
        verifyDirectDisabled
    } = useVerificationPresentation({
        source,
        verification,
        verificationError,
        issuerName,
        verifiedSource,
        address,
        connectedWalletAddress,
        alreadyRegistered,
        verifyStatus,
        retryReady,
        findStatus,
        sourceResolving
    });

    // An existing wallet keeps its rewards destination (an empty pick is
    // omitted), so show the address actually paid; a new wallet pays its
    // holding address: the EOA, or the Squads vault.
    const sourceWalletAddress = verifiedSource ? sourceAddress(verifiedSource) : (resolvedMultisig?.address ?? address);
    const currentWalletDestination = existingPositions.find(
        (position) => position.account.address === sourceWalletAddress
    )?.rewardsDestinationAddress;
    const holdingRewardsAddress =
        verifiedSource !== null
            ? defaultRewardsDestinationAddress(verifiedSource)
            : sourceKind === "eoa"
              ? connectedWalletAddress || address
              : (resolvedMultisig?.vaultAddress ?? resolvedMultisig?.address ?? address);
    const defaultRewardsAddress = currentWalletDestination || holdingRewardsAddress;
    // Its name: the saved destination's label, else the holding address's
    // name. A kept destination that isn't saved has none.
    const savedDefault = findSavedDestination(savedDestinations, defaultRewardsAddress);
    const defaultRewardsLabel = savedDefault
        ? savedDestinationLabel(savedDefault)
        : defaultRewardsAddress !== holdingRewardsAddress
          ? ""
          : sourceKind === "multisig-squads"
            ? MULTISIG_VAULT_LABEL
            : SAME_AS_SOURCE_LABEL;

    // The last step's enrollment receipt — position count, total USDv, and the
    // truncated rewards destination. Everything is already in state; this only
    // shapes it for display.
    const submitReview = resolveSubmitReview({
        foundPosition,
        verifiedSource,
        destination,
        defaultDestinationAddress: defaultRewardsAddress,
        defaultDestinationLabel: defaultRewardsLabel,
        savedDestinations
    });

    const nav = useStepNavigation(stepGateState, foundPosition);

    return {
        source: {
            kind: sourceKind,
            connectedWalletAddress: connectedWalletAddress || null,
            multisigSummary,
            address,
            addressTouched,
            onAddressChange,
            onAddressBlur,
            addressLabel,
            addressPlaceholder,
            addressInvalid,
            addressError,
            alreadyRegistered,
            showRetry: showSourceRetry,
            onRetry: handleSourceRetry,
            notice: sourceNotice
        },
        find: {
            status: findStatus,
            message: findMessage,
            messageTone: findMessageTone,
            // Delay the rendered preview only; navigation and the submission
            // receipt above keep the actual settled discovery/balance state.
            addressPreview: sourceResolving ? null : addressPreview,
            foundPosition: sourceResolving ? null : foundPosition
        },
        verify: {
            status: verifyStatus,
            message: verifyMessage,
            messageTone: verifyMessageTone,
            actionLabel: verifyActionLabel,
            actionDisabled: verifyActionDisabled,
            directLabel: verifyDirectLabel,
            isDirect: verificationIsDirect,
            retryReady,
            onVerify: handleVerify,
            directDisabled: verifyDirectDisabled,
            onVerifyDirect: handleVerifyDirect,
            manualReview: verificationIsDirect
                ? { description: manualReviewDescription, onDescriptionChange: setManualReviewDescription }
                : undefined
        },
        rewards: {
            destination,
            onDestinationChange: setDestination,
            defaultLabel: defaultRewardsLabel,
            defaultAddress: defaultRewardsAddress
        },
        submit: {
            automaticEnrollment: !verificationIsDirect,
            pendingReview,
            onSubmit: handleSubmit,
            claimRequired,
            onConfirmClaim: handleConfirmClaim,
            isSubmitting,
            error: submitError,
            registeredPositionCount,
            submitted,
            review: submitReview,
            onStartOver: (): void => {
                onAddressChange("");
                setAddressTouched(false);
                nav.setActive(0);
            },
            reverify: verificationExpired
                ? {
                      status:
                          verifyStatus === "verifying" ? "verifying" : verifyStatus === "failed" ? "failed" : "idle",
                      retryReady,
                      reason: recoveryReason ?? "expired",
                      onVerifyAgain: handleVerifyAgain
                  }
                : null
        },
        nav
    };
}
